import { useRef } from 'react';
import { Box, Chip, Paper, Theme, Typography, useMediaQuery } from '@material-ui/core';
import FlowLine from './FlowLine';
import { makeStyles } from '@material-ui/core/styles';
import {
  darkGreen,
  dividerColor,
  primaryGreen,
  textDisabled,
  white,
  primaryText,
} from 'src/data/colors';
import { useFlowPosition } from './useFlowPosition';
import Status, { StatusProps } from './Status';
import { Equipment } from 'src/models';
import CardEquipment from './CardEquipment';
import { theme } from 'src/components/theme';
import identifiers from 'src/tests/identifiers';
import CardLink from './CardLink';
import { borderRadius } from 'src/data/borders';
const getCardZIndex = (flow: number): number => {
  return flow > 0 ? 5 : flow < 0 ? 3 : 0;
};

const getCardColor = (flow?: number) => {
  return flow && flow > 0 ? primaryGreen : darkGreen;
};

const getCardColors = (installed: boolean, flow?: number, percentage?: number) => {
  if (!installed) {
    return {
      borderColor: dividerColor,
      typographyColor: textDisabled,
      statusColor: white,
      flowColor: dividerColor,
      chipTypographyColor: textDisabled,
    };
  }

  const statusNotActiveColor = percentage && percentage > 0 ? primaryGreen : white;
  return {
    borderColor: flow && Math.abs(flow) > 0 ? getCardColor(flow) : dividerColor,
    typographyColor: flow && Math.abs(flow) > 0 ? getCardColor(flow) : primaryText,
    statusColor: flow && Math.abs(flow) > 0 ? getCardColor(flow) : statusNotActiveColor,
    flowColor: flow && Math.abs(flow) > 0 ? getCardColor(flow) : dividerColor,
    chipTypographyColor: primaryText,
  };
};

interface CardProps {
  siteId: number;
  componentId: string;
  icon?: string;
  title: string;
  badge?: string;
  installed: boolean;
  className?: string;
  status?: Omit<StatusProps, 'color'>;
  gridArea?: string;
  linkTagsIds?: string[];
  footerText?: string[];
  powerFlow?: {
    endX: number;
    endY: number;
    flow: number;
    active?: boolean;
    invertFlow?: boolean;
    cornerNearStart?: boolean;
    halfPath?: boolean;
  };
  equipments?: Equipment[][];
}

const useStyles = makeStyles<
  Theme,
  { border: boolean; borderColor: string; typographyColor: string; chipTypographyColor: string }
>(() => ({
  cardContainer: {
    width: '100%',
  },
  icon: {
    height: 48,
    width: 48,
    marginRight: 10,
  },
  paper: {
    border: ({ borderColor }) => `1px solid ${borderColor}`,
    width: '100%',
    position: 'relative',
    maxWidth: '256px',
    boxShadow: '0px 4px 8px -4px rgba(0, 0, 0, 0.16), 0px 8px 12px -4px rgba(0, 0, 0, 0.08)',
    borderRadius: borderRadius.md,
  },
  cardMask: {
    backgroundColor: 'white',
    width: '100%',
    height: '100%',
    position: 'relative',
    zIndex: 10,
    borderRadius: borderRadius.md,
  },
  title: {
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    color: ({ typographyColor }) => typographyColor,
  },
  header: ({ border }) => ({
    borderBottom: border ? `1px solid ${dividerColor}` : '',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 10px',
    justifyContent: 'space-between',
  }),
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  chip: {
    color: ({ chipTypographyColor }) => chipTypographyColor,
    '@media (max-width: 712px)': {
      fontSize: 10,
    },
  },
  footer: {
    display: 'flex',
    padding: '0 10px',
    flexDirection: 'column',
    paddingBottom: 4,
  },
  footerText: {
    fontSize: 12,
    color: theme.palette.text.primary,
  },
  equipmentContainer: {
    padding: '0 10px',
    marginTop: 8,
    '@media (max-width: 712px)': {
      display: 'none',
    },
  },
}));

const Card = ({
  siteId,
  componentId,
  icon,
  title,
  badge,
  installed,
  className,
  powerFlow,
  status,
  equipments,
  gridArea,
  linkTagsIds,
  footerText,
}: CardProps) => {
  const { borderColor, typographyColor, statusColor, flowColor, chipTypographyColor } =
    getCardColors(installed, powerFlow?.flow, status?.percentage);

  const classes = useStyles({
    border: !!status,
    borderColor,
    typographyColor,
    chipTypographyColor,
  });
  const localPaperRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const isSmallScreen = useMediaQuery('(max-width: 712px)');
  const { startEndCoords, offset } = useFlowPosition({
    endX: powerFlow?.endX,
    endY: powerFlow?.endY,
    elementRef: localPaperRef,
    svgRef,
    cornerNearStart: powerFlow?.cornerNearStart,
    halfPath: powerFlow?.halfPath,
    isSmallScreen: isSmallScreen,
  });

  return (
    <Paper
      data-testid={identifiers.dashboardPage.flowComponent + '-' + componentId}
      className={`${className || ''} ${classes.paper}`}
      ref={localPaperRef}
      variant="outlined"
      style={{
        gridArea: gridArea,
        zIndex: getCardZIndex(powerFlow?.flow || 0),
        position: 'relative',
      }}
    >
      <CardLink linkTagsIds={linkTagsIds} siteId={siteId} componentId={componentId}>
        <Box className={classes.cardMask}>
          <Box className={classes.header}>
            <div className={classes.headerLeft}>
              <img src={icon} alt={title} className={classes.icon} />
              {title && (
                <Typography className={classes.title} noWrap={false}>
                  {title}
                </Typography>
              )}
            </div>
            {badge && (
              <Chip
                label={badge}
                className={classes.chip}
                size={isSmallScreen ? 'small' : 'medium'}
                style={{ cursor: linkTagsIds && linkTagsIds.length > 0 ? 'pointer' : 'default' }}
              />
            )}
          </Box>
          {equipments && (
            <div className={classes.equipmentContainer}>
              <CardEquipment equipments={equipments} />
            </div>
          )}
          {status && <Status {...status} color={statusColor} installed={installed} />}
          {footerText && (
            <Box className={classes.footer}>
              {footerText?.map(
                (text) =>
                  text && (
                    <Typography className={classes.footerText} key={text}>
                      {text}
                    </Typography>
                  )
              )}
            </Box>
          )}
        </Box>
      </CardLink>

      <FlowLine
        componentId={componentId}
        ref={svgRef}
        start={startEndCoords.start}
        end={startEndCoords.end}
        cornerNearStart={powerFlow?.cornerNearStart}
        color={flowColor}
        rate={powerFlow?.flow || 0}
        disabled={!powerFlow?.active}
        canvasOffset={{
          top: offset.y,
          left: offset.x,
        }}
      />
    </Paper>
  );
};

export default Card;
